import React from 'react';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { PostWSFolder, PatchWSFolder } from '../../../redux/dataroomSlice';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import styles from '../css/vdr.module.css';

/**
 * Create / Edit folder — Figma 6971:23920 / 6971:15389.
 */
const examplesToText = value => {
    if (!Array.isArray(value)) return '';
    return value
        .map(item => String(item || '').trim())
        .filter(Boolean)
        .join('\n');
};

const textToExamples = value =>
    String(value || '')
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);

const CreateFolderModal = ({ open, onClose, onSaved, workstream, workstreams = [], folders = [], parentFolder, editFolder }) => {
    const dispatch = useDispatch();
    const { folderBtnLoading } = useSelector(state => state?.dataroom);
    const [selectedWsId, setSelectedWsId] = React.useState('');
    const [parentId, setParentId] = React.useState('');
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [examples, setExamples] = React.useState('');
    React.useEffect(() => {
        if (open) {
            setSelectedWsId(String(editFolder ? workstream?.id : workstream?.id || workstreams?.[0]?.id || ''));
            setParentId(editFolder ? (editFolder.parent || editFolder.parent_id || '') : parentFolder?.id || '');
            setName(editFolder?.name || '');
            setDescription(editFolder?.description || '');
            setExamples(examplesToText(editFolder?.example_documents));
        }
    }, [open, editFolder, workstream, workstreams, parentFolder]);

    if (!open) return null;

    const activeWsId = selectedWsId || workstream?.id;
    const parentOptions = (folders || []).filter(f => String(f.id) !== String(editFolder?.id));

    const handleSubmit = async () => {
        if (!name.trim() || !activeWsId) return;
        const bodyBase = {
            name: name.trim(),
            description,
            workstream: Number(activeWsId) || activeWsId,
        };
        if (editFolder) {
            const result = await dispatch(
                PatchWSFolder({
                    ...bodyBase,
                    id: editFolder.id,
                    example_documents: textToExamples(examples),
                    ...(parentId ? { parent: parentId } : {}),
                }),
            );
            if (!PatchWSFolder.fulfilled.match(result)) return;
            onSaved?.(result.payload?.data);
        } else {
            const result = await dispatch(
                PostWSFolder({
                    ...bodyBase,
                    example_documents: textToExamples(examples),
                    requested: true,
                }),
            );
            if (!PostWSFolder.fulfilled.match(result)) return;
        }
        onClose();
    };

    return (
        <div className={styles.overlay} role="dialog" aria-modal="true">
            <div className={`${styles.modal} ${styles.modalTall}`}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{editFolder ? 'Edit folder' : 'Create folder'}</h2>
                    <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close">
                        <Dismiss20Regular />
                    </button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.formField}>
                        <label className={styles.fieldLabel}>Workstream</label>
                        <div className={styles.selectWrap}>
                            <select
                                className={styles.input}
                                value={selectedWsId}
                                onChange={e => setSelectedWsId(e.target.value)}
                                disabled={!!editFolder}
                            >
                                {(workstreams || []).map(ws => (
                                    <option key={ws.id} value={ws.id}>
                                        {ws.name}
                                    </option>
                                ))}
                                {!workstreams?.length && workstream && (
                                    <option value={workstream.id}>{workstream.name}</option>
                                )}
                            </select>
                        </div>
                    </div>

                    {editFolder && (
                        <div className={styles.formField}>
                            <label className={styles.fieldLabel}>Parent folder</label>
                            <div className={styles.selectWrap}>
                                <select className={styles.input} value={parentId || ''} onChange={e => setParentId(e.target.value)}>
                                    <option value="">None (top level)</option>
                                    {parentOptions.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className={styles.formField}>
                        <label className={styles.fieldLabel}>
                            Folder name <span className={styles.requiredMark}>*</span>
                        </label>
                        <input
                            className={styles.input}
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="e.g. Disaster Recovery"
                        />
                    </div>

                    <div className={styles.formField}>
                        <label className={styles.fieldLabel}>Description</label>
                        <textarea className={`${styles.textarea} ${styles.textareaShort}`} value={description} onChange={e => setDescription(e.target.value)} />
                    </div>

                    <div className={styles.formField}>
                        <label className={styles.fieldLabel}>Example documents</label>
                        <textarea
                            className={`${styles.textarea} ${styles.textareaShort}`}
                            value={examples}
                            onChange={e => setExamples(e.target.value)}
                            placeholder="One per line"
                        />
                    </div>
                </div>
                <div className={styles.modalFooter}>
                    <button type="button" className={styles.primaryBtn} onClick={handleSubmit} disabled={folderBtnLoading || !name.trim()}>
                        {editFolder ? 'Save changes' : 'Create folder'}{' '}
                        {folderBtnLoading && <AiOutlineLoading3Quarters className="rotateLoader" />}
                    </button>
                    <button type="button" className={`${styles.secondaryBtn} ${styles.modalCancel}`} onClick={onClose}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateFolderModal;
